import { isAxiosError } from "axios";

import {
  ALLOWED_FILE_EXTENSIONS,
  ALLOWED_FILE_MIME_TYPES,
  DOCUMENT_FILE_EXTENSIONS,
  IMAGE_FILE_EXTENSIONS,
  MAX_CONTEXT_FILES_SIZE_BYTES,
  MAX_CONTEXT_FILES_SIZE_MB,
} from "./constants";
import { ContextFile } from "@/common/components/wavvest-planning/types.ts";
import { uploadContextFile } from "@/api/weave-x";
import canvasWebSocket from "@/services/websocket-service";

export const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleString();
};

export const isFileTypeAllowed = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  const hasAllowedExtension = ALLOWED_FILE_EXTENSIONS.some((ext) =>
    fileName.endsWith(ext),
  );

  const hasAllowedMimeType = ALLOWED_FILE_MIME_TYPES.includes(file.type);

  return hasAllowedExtension && hasAllowedMimeType;
};

export const filterAllowedFiles = (
  files: File[],
): {
  allowedFiles: File[];
  rejectedCount: number;
} => {
  const allowedFiles = files.filter((file) => isFileTypeAllowed(file));
  const rejectedCount = files.length - allowedFiles.length;

  return {
    allowedFiles,
    rejectedCount,
  };
};

export const judgeFileType = (file: File): "document" | "image" | "unknown" => {
  const fileName = file.name.toLowerCase();
  if (DOCUMENT_FILE_EXTENSIONS.some((ext) => fileName.endsWith(ext))) {
    return "document";
  }
  if (IMAGE_FILE_EXTENSIONS.some((ext) => fileName.endsWith(ext))) {
    return "image";
  }
  return "unknown";
};

export const judgeFileTypeByName = (
  fileName: string,
): "document" | "image" | "unknown" => {
  const lowerFileName = fileName.toLowerCase();
  if (DOCUMENT_FILE_EXTENSIONS.some((ext) => lowerFileName.endsWith(ext))) {
    return "document";
  }
  if (IMAGE_FILE_EXTENSIONS.some((ext) => lowerFileName.endsWith(ext))) {
    return "image";
  }
  return "unknown";
};

export const readFileAsText = async (file: File): Promise<string> => {
  const fileReader = new FileReader();
  return new Promise((resolve, reject) => {
    fileReader.readAsText(file);
    fileReader.onload = (e) => {
      const content = e.target?.result as string;
      resolve(content);
    };
    fileReader.onerror = (e) => {
      reject(e);
    };
  });
};

export const readFileAsBase64 = async (file: File): Promise<string> => {
  const fileReader = new FileReader();
  return new Promise((resolve, reject) => {
    fileReader.readAsDataURL(file);
    fileReader.onload = (e) => {
      const content = e.target?.result as string;
      resolve(content.split(",")[1]);
    };
    fileReader.onerror = (e) => {
      reject(e);
    };
  });
};

/**
 * Asynchronously processes an array of files and generates an array of context files.
 *
 * This function filters and processes the provided files to generate the resulting
 * array of `ContextFile` objects based on specific criteria, such as size limits.
 *
 * @param {File[]} files - An array of File objects to be processed.
 * @returns {Promise<ContextFile[]>} A Promise that resolves to an array of ContextFile objects.
 *
 */
export const processAddingContextFiles = async (
  files: File[],
): Promise<{
  contextFiles: ContextFile[];
  errors: string[];
}> => {
  const contextFiles: Array<ContextFile> = [];
  const errors: string[] = [];

  const { allowedFiles, rejectedCount } = filterAllowedFiles(files);

  if (rejectedCount > 0) {
    errors.push(
      `${rejectedCount} file(s) were rejected due to unsupported file types`,
    );
  }

  let currentTotalSize = 0;

  for (const file of allowedFiles) {
    currentTotalSize += file.size;
    if (currentTotalSize > MAX_CONTEXT_FILES_SIZE_BYTES) {
      errors.push(
        `Total size of files is too large (max ${MAX_CONTEXT_FILES_SIZE_MB}MB)`,
      );
      break;
    }

    try {
      const content = await readFileAsBase64(file);
      const fileType = judgeFileType(file);

      if (fileType === "unknown") {
        errors.push(`File "${file.name}" has unsupported file type`);
        continue;
      }

      contextFiles.push({
        fileName: file.name,
        content,
        type: fileType,
      });
    } catch (error) {
      errors.push(
        `Failed to process file "${file.name}": ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  return {
    contextFiles,
    errors,
  };
};

/**
 * Process files for immediate upload and return ContextFiles with uploading state
 * This function processes files and returns them immediately with uploading=true,
 * then uploads them in the background
 */
export const processFilesForImmediateUpload = async (
  files: File[],
  userId: string,
  onFileUpdate: (fileName: string, updates: Partial<ContextFile>) => void,
  currentContextFiles?: ContextFile[],
): Promise<{
  contextFiles: ContextFile[];
  errors: string[];
}> => {
  const contextFiles: Array<ContextFile> = [];
  const errors: string[] = [];

  // Ensure session exists before processing any files
  let sessionId = canvasWebSocket.getSessionId();
  if (!sessionId) {
    try {
      await canvasWebSocket.createNewSession();
      sessionId = canvasWebSocket.getSessionId();
    } catch {
      errors.push("Failed to create session for file upload");
      return { contextFiles, errors };
    }
  }

  if (!sessionId) {
    errors.push("No active session available for file upload");
    return { contextFiles, errors };
  }

  const { allowedFiles, rejectedCount } = filterAllowedFiles(files);

  if (rejectedCount > 0) {
    errors.push(
      `${rejectedCount} file(s) were rejected due to unsupported file types`,
    );
  }

  // Calculate existing files total size
  let currentTotalSize = (currentContextFiles || []).reduce((total, file) => {
    // Estimate size from base64 content (base64 is ~1.33x larger than original)
    const originalSize = file.content
      ? Math.floor((file.content.length * 3) / 4)
      : 0;
    return total + originalSize;
  }, 0);
  const attachedFileNames = new Set(
    (currentContextFiles || []).map((file) => file.fileName),
  );

  for (const file of allowedFiles) {
    if (attachedFileNames.has(file.name)) {
      errors.push(
        `Cannot add "${file.name}". A file with this name is already attached.`,
      );
      continue;
    }

    // Check if adding this specific file would exceed the limit
    if (currentTotalSize + file.size > MAX_CONTEXT_FILES_SIZE_BYTES) {
      const remainingSpace = MAX_CONTEXT_FILES_SIZE_BYTES - currentTotalSize;
      const remainingSpaceMB = (remainingSpace / (1024 * 1024)).toFixed(1);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      errors.push(
        `Cannot add "${file.name}" (${fileSizeMB}MB). Only ${remainingSpaceMB}MB remaining of ${MAX_CONTEXT_FILES_SIZE_MB}MB limit.`,
      );
      continue; // Skip this file but continue with others
    }

    try {
      const content = await readFileAsBase64(file);
      const fileType = judgeFileType(file);

      if (fileType === "unknown") {
        errors.push(`File "${file.name}" has unsupported file type`);
        continue;
      }

      // Create context file with uploading state
      const contextFile: ContextFile = {
        fileName: file.name,
        content,
        type: fileType,
        uploading: true,
        uploaded: false,
      };

      contextFiles.push(contextFile);
      attachedFileNames.add(file.name);

      // Update current total size for next file check
      currentTotalSize += file.size;

      // Start upload in background with the same sessionId
      uploadFileInBackground(
        file.name,
        content,
        userId,
        onFileUpdate,
        sessionId,
      );
    } catch (error) {
      errors.push(
        `Failed to process file "${file.name}": ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  return {
    contextFiles,
    errors,
  };
};

/**
 * Upload a single file in the background and update its status
 */
const uploadFileInBackground = async (
  fileName: string,
  content: string,
  userId: string,
  onFileUpdate: (fileName: string, updates: Partial<ContextFile>) => void,
  sessionId: string,
) => {
  try {
    await uploadContextFile({
      userId,
      sessionId,
      files: [
        {
          fileName,
          fileContent: content,
        },
      ],
    });

    // Update file status to uploaded
    onFileUpdate(fileName, {
      uploading: false,
      uploaded: true,
      fileKey: fileName,
    });
  } catch (error) {
    const errorMessage =
      isAxiosError(error) && error.response?.status === 409
        ? `Cannot add "${fileName}". A file with this name is already attached.`
        : error instanceof Error
          ? error.message
          : "Upload failed";

    // Update file status to error
    onFileUpdate(fileName, {
      uploading: false,
      uploaded: false,
      error: errorMessage,
    });
  }
};

export function utf8ToBase64(str: string): string {
  const utf8Bytes = new TextEncoder().encode(str);
  const binary = utf8Bytes.reduce(
    (acc, byte) => acc + String.fromCharCode(byte),
    "",
  );
  return btoa(binary);
}
